import { Auth } from 'Shared/auth';
import { MediaTarget } from './target';
import { MediaItem } from './item';
import MediaItemMenu from './item_menu';

onDomReady(() => {
   MediaManager.initialize();
});

/**
 * See docs in the README for this directory
 */

export const MediaManager = (function () {
   let mediaItemMenu;
   let attachedItems = {};

   MediaItem.addEvent('toggleMenu', (mediaItem, menuAnchor) => {
      mediaItemMenu.toggleForMediaItem(mediaItem, menuAnchor);
   });

   function setupImageMarkersDialog() {
      import('Shared/modal').then(({ Modal }) => {
         Modal.addEvents({
            onImageMarkersLoad: function (contentBox, options, responseData) {
               ImageMarkers.initialize(contentBox, options, responseData);
            },
            onImageMarkersUnload: function () {
               ImageMarkers.unload();
            },
         });
      });
   }

   let publicInterface = {
      initialize: function () {
         MediaTarget.registerAllTargets();

         setupMediaItemMenu();
      },

      // When a mediaItem is selected from Media Manager, it is appended to
      // `attachedItems`. When it is `detach`ed, it goes back into the list of
      // media.
      detach: function (mediaItem) {
         Auth.required({
            onAuthorize: function () {
               delete attachedItems[mediaItem.getGlobalID()];
               mediaItem.deleted();
            },
         });
      },

      /**
       * Open the Media Manager for a target and run the target's attach
       * protocol on whatever the person picks. Every attachment point funnels
       * through here, both MediaTarget's browse event and direct
       * MinimalMediaTarget callers.
       */
      browse: function (mediaTarget) {
         // Without a session the modal would open onto a wall of 401s from the
         // library endpoints.
         if (!Auth.isLoggedIn()) {
            Auth.required({
               onAuthorize: () => MediaManager.browse(mediaTarget),
            });
            return;
         }

         let attach = function (mediaItem) {
            mediaTarget.attachItem(mediaItem, (oldMediaItem, success, errorMessage) => {
               if (!success) {
                  // The modal is already closed, so surface the veto the same
                  // way the customAttach targets do.
                  if (errorMessage) {
                     alert(errorMessage);
                  }
                  return;
               }
               if (oldMediaItem) {
                  delete attachedItems[oldMediaItem.getGlobalID()];
               }
               mediaItem.whenReady(() => {
                  attachedItems[mediaItem.getGlobalID()] = true;
               });
            });
         };

         // Open a legacy edit dialog for a v2 tile. The React modal has already
         // been unmounted by openMediaManager (its Radix layer would otherwise
         // freeze these MooTools dialogs), so they're clickable. Returns
         // { closed, saved }: `closed` reopens the modal, `saved` fires when the
         // server write lands so the tile stops spinning and shows the result.
         let editItem = (item, action) => {
            let resolveClosed;
            let resolveSaved;
            let closed = new Promise(resolve => {
               resolveClosed = resolve;
            });
            let saved = new Promise(resolve => {
               resolveSaved = resolve;
            });

            if (action === 'crop') {
               // The crop dialog closes when the crop *request* is accepted;
               // the image itself comes from a background job, and the item's
               // dataPromise resolves once cropImageStatus reports it. On
               // cancel no item is passed and there's nothing to wait for.
               ImageCrop.showForMediaItem(MediaItem.createFromData(item), mediaTarget, cropped => {
                  resolveClosed();
                  if (cropped) {
                     cropped.whenReady(() => resolveSaved());
                  } else {
                     resolveSaved();
                  }
               });
            } else {
               // The markers dialog loads its canvas source via
               // getSrc('medium'), but the library item only carries the sizes
               // stored as renditions and often omits medium. Derive it from
               // another size (the image server makes any size on demand);
               // without a source the fabric canvas never inits and the dialog's
               // key handlers throw.
               let source = item.medium || item.standard || item.original || item.thumbnail;
               // Swap only a real extension after the last dot in the file
               // name; a dotless URL passes through unchanged rather than
               // letting the pattern eat into the hostname.
               let withMedium = item.medium
                  ? item
                  : { ...item, medium: source && source.replace(/\.[^./]+$/, '.medium') };
               let mediaItem = MediaItem.createFromData(withMedium);
               import('Shared/modal').then(({ Modal }) => {
                  Modal.open({
                     type: 'module',
                     name: 'ImageMarkers',
                     boxClass: 'mediaLibraryModalBox editPhoto',
                     clientOptions: { mediaImage: mediaItem },
                     serverOptions: { imageid: mediaItem.getID() },
                     // Markers pops the dialog before its save POST (which makes
                     // a *new* image) finishes: reopen on close, and whenReady
                     // resolves when the write lands (or now, on cancel).
                     onClose: () => {
                        resolveClosed();
                        mediaItem.whenReady(() => resolveSaved());
                     },
                  });
               });
            }

            return { closed, saved };
         };

         import('Shared/MediaManager').then(({ openMediaManager }) => {
            openMediaManager({
               accepts: mediaTarget.getMediaFilterName(),
               hiddenGlobalIds: Object.keys(attachedItems),
               // Only MinimalMediaTarget answers this; the DOM-bound targets
               // always have somewhere to put the item.
               browseOnly: mediaTarget.isBrowseOnly?.() ?? false,
               editItem: editItem,
            }).then(item => {
               if (!item) {
                  return;
               }
               let mediaItem = MediaItem.createFromData(item);
               // A crop-required image: the modal has closed, so the legacy
               // cropperjs dialog opens clickable; attach the cropped result.
               if (item.filter_state === 'crop') {
                  ImageCrop.showForMediaItem(mediaItem, mediaTarget, cropped => {
                     if (cropped) {
                        // Wait for the crop job before attaching: the loading
                        // item's filter_state is stripped, and strict targets
                        // (guide edit's customAttach) veto anything that isn't
                        // explicitly passing. The resolved data carries the
                        // server-computed state for this target's filter.
                        //
                        // That wait is server-side work with no feedback — the
                        // target just sits on its old image. Show its loading
                        // overlay now so the user sees a spinner over the
                        // image; attach() -> _displayItem takes the overlay
                        // over and clears it once the cropped image renders.
                        mediaTarget._displayLoading?.(true);
                        cropped.whenReady(() => attach(cropped));
                     } else {
                        // Cancelled: the modal is unmounted, so reopen it
                        // instead of leaving the person stranded on the page.
                        MediaManager.browse(mediaTarget);
                     }
                  });
               } else {
                  attach(mediaItem);
               }
            });
         });
      },
   };

   /**
    * Setup event handlers for the MediaItemMenu
    *
    * For most of these actions, we don't really care about completion / cancel
    * events cause dialogs do Modal.pop() and leave us right where we started.
    */
   function setupMediaItemMenu() {
      let self = this;
      mediaItemMenu = new MediaItemMenu();
      let menuItems = mediaItemMenu.menuItems;

      setupImageMarkersDialog(menuItems);

      menuItems.addEvents({
         markers: function (mediaItem) {
            Auth.required({
               onAuthorize: function () {
                  let clientOptions = { mediaImage: mediaItem };
                  import('Shared/modal').then(({ Modal }) => {
                     Modal.open({
                        type: 'module',
                        name: 'ImageMarkers',
                        boxClass: 'mediaLibraryModalBox editPhoto',
                        clientOptions: clientOptions,
                        serverOptions: {
                           imageid: mediaItem.getID(),
                        },
                     });
                  });
               },
            });
         },
         crop: function (mediaItem) {
            Auth.required({
               onAuthorize: function () {
                  // An item attached to a target crops to that target's
                  // filter, so the dialog enforces its aspect ratio.
                  let itemContext = mediaItem.getContext();
                  let mediaTarget = instanceOf(itemContext, MediaTarget) ? itemContext : null;

                  ImageCrop.showForMediaItem(mediaItem, mediaTarget);
               },
            });
         },
         deleted: function (mediaItem) {
            publicInterface.detach(mediaItem);
         },
         fullsize: function (mediaItem) {
            // Get image source URL, remove '.thumbnail' suffix, and open
            // it in a new window.
            let imageURL = mediaItem.data.original();
            window.open(imageURL);
         },
         copy: function (mediaItem) {
            Auth.required({
               onAuthorize: mediaItem.copyToMediaLibrary.bind(mediaItem),
            });
         },
      });
   }

   return publicInterface;
})();
